import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { fetchChecklistTypes } from "../../../../api/quality/checklistType";
import { createChecklist, fetchChecklists } from "../../../../api/quality/checklist";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

async function fetchChecklistCountForFilter(filter) {
  if (!filter) return 0;
  try {
    const res = await fetchChecklists({
      filters: [filter],
      per_page: 1,
      page: 1,
      sortBy: "created_at",
      sort: "DESC",
    });
    return res?.totalCount || 0;
  } catch (err) {
    console.warn("Could not fetch existing checklists count:", err);
    return 0;
  }
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {string|number} [props.vehicleId]
 * @param {object} [props.vehicle]
 * @param {string|number} [props.projectId]
 * @param {object} [props.project]
 * @param {string|number} [props.customerId]
 * @param {object} [props.customer]
 * @param {string|number} [props.folderId] — project folder; pair with projectId when linking
 * @param {object} [props.folder]
 * @param {object} [props.report] — service report; default title matches server (Rapport …)
 * @param {string|null|undefined} [props.defaultTitle] — if set (including ""), skips auto title generation
 * @param {function} [props.onChange] — (checklistType, { title }) => void; title is trimmed
 */
export const ChecklistTypeSelector = ({
  open,
  onClose,
  vehicleId,
  vehicle,
  projectId,
  project,
  customerId,
  customer,
  folderId,
  folder,
  report,
  defaultTitle,
  onChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDraftTitle("");

    const getCountFilter = () => {
      if (folderId) return { name: "folder_id", value: folderId };
      if (vehicleId) return { name: "vehicle_id", value: vehicleId };
      if (projectId) return { name: "project", value: projectId };
      if (customerId) return { name: "customer", value: customerId };
      return null;
    };

    const resolveDefaultTitle = async () => {
      if (defaultTitle !== undefined && defaultTitle !== null) {
        return String(defaultTitle);
      }
      if (report?.id) {
        return t("Report checklist default title", {
          number: report.report_number ?? "",
          address: report.address ?? "",
        });
      }

      const filter = getCountFilter();
      const existing = filter ? await fetchChecklistCountForFilter(filter) : 0;
      const checklistNumber = existing + 1;

      if (folderId) {
        const folderRef = folder?.displayTitle || folder?.title || "";
        return t("Folder checklist title", {
          number: checklistNumber,
          suffix: folderRef ? ` - ${folderRef}` : "",
        });
      }
      if (vehicleId) {
        const vehiclePlate = vehicle?.number_plate || "";
        return `Intern kjøretøykontroll #${checklistNumber}${
          vehiclePlate ? ` - ${vehiclePlate}` : ""
        }`;
      }
      if (projectId) {
        const projectRef =
          project?.displayTitle || project?.project_number || project?.name || "";
        return t("Project checklist title", {
          number: checklistNumber,
          suffix: projectRef ? ` - ${projectRef}` : "",
        });
      }
      if (customerId) {
        const customerRef =
          customer?.displayTitle ||
          customer?.name ||
          customer?.customer_number ||
          "";
        return t("Customer checklist title", {
          number: checklistNumber,
          suffix: customerRef ? ` - ${customerRef}` : "",
        });
      }

      return t("Generic new checklist title", {
        number: checklistNumber,
        date: new Date().toISOString().split("T")[0],
      });
    };

    (async () => {
      try {
        const [typesResponse, titleStr] = await Promise.all([
          fetchChecklistTypes().then((response) => response.data?.data || []),
          resolveDefaultTitle(),
        ]);
        if (cancelled) return;
        setChecklistTypes(typesResponse);
        setDraftTitle(titleStr);
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load checklist types");
          console.error("Error loading checklist types:", err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally only re-run when dialog opens; avoids resetting while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on open only
  }, [open]);

  const contextHint = () => {
    if (report?.id) {
      return t("Choose the type of checklist you want to create for this report");
    }
    if (folderId) {
      return t("Choose the type of checklist you want to create for this folder");
    }
    if (projectId) {
      return t("Choose the type of checklist you want to create for this project");
    }
    if (vehicleId) {
      return t("Choose the type of checklist you want to create for this vehicle");
    }
    if (customerId) {
      return t("Choose the type of checklist you want to create for this customer");
    }
    return t("Choose the type of checklist you want to create");
  };

  const handleSelectChecklistType = async (checklistType) => {
    const title = String(draftTitle || "").trim();
    if (!title) {
      toast.error(t("Title is required"));
      return;
    }

    if (onChange) {
      onChange(checklistType, { title });
      onClose();
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        checklist_type_id: checklistType.id,
        title,
        description: checklistType.description || "",
        date: new Date().toISOString().split("T")[0],
        status: "open",
      };
      if (vehicleId) payload.vehicle_id = vehicleId;
      if (projectId) payload.project_id = projectId;
      if (customerId) payload.customer_id = customerId;
      if (folderId) payload.folder_id = folderId;

      const response = await createChecklist(payload);

      const checklistId = response?.id || response?.data?.id;

      if (checklistId) {
        toast.success(t("Checklist created successfully"));
        navigate(`/dashboard/hse-ks/checklists/${checklistId}`);
        onClose();
      } else {
        console.error("Checklist response:", response);
        throw new Error("Checklist ID not found in response");
      }
    } catch (err) {
      console.error("Error creating checklist:", err);
      toast.error(t("Failed to create checklist"));
      setError("Failed to create checklist");
    } finally {
      setIsCreating(false);
    }
  };

  const titleInvalid = !String(draftTitle || "").trim();

  const renderContent = () => {
    if (isLoading || isCreating) {
      return (
        <div className="text-center py-8">
          <ResourceLoading />
          {isCreating && (
            <p className="mt-4 text-sm text-gray-500">
              {t("Creating checklist...")}
            </p>
          )}
        </div>
      );
    }

    if (error) {
      return <ResourceError errors={[error]} />;
    }

    if (!checklistTypes.length) {
      return <ResourceEmpty text={t("No checklist types available")} />;
    }

    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("Select Checklist Type")}
          </h3>
          <p className="text-sm text-gray-500">{contextHint()}</p>
        </div>

        <div>
          <label
            htmlFor="checklist-create-title"
            className="block text-sm font-medium text-gray-700 text-left mb-1"
          >
            {t("Title")}
          </label>
          <input
            id="checklist-create-title"
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder={t("Checklist title placeholder")}
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-gray-500 text-left">
            {t("Checklist title edit hint")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {checklistTypes.map((checklistType) => (
            <button
              key={checklistType.id}
              type="button"
              onClick={() => handleSelectChecklistType(checklistType)}
              disabled={isCreating || titleInvalid}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {checklistType.title}
                  </h4>
                  {checklistType.description && (
                    <p className="text-sm text-gray-500 mt-1">
                      {checklistType.description}
                    </p>
                  )}
                </div>
                <div className="ml-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {checklistType.items?.length || 0} {t("items")}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      as="div"
      className="fixed inset-0 z-50 overflow-y-auto"
      open={open}
      onClose={onClose}
    >
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
          <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <span className="sr-only">{t("Close")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {renderContent()}

          <div className="mt-5 sm:mt-6">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              onClick={onClose}
            >
              {t("Cancel")}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
